/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Row,
  Table,
  Card,
  CardBody,
  Collapse,
} from 'reactstrap';

import { CompanyId } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import reportService from '../../services/report';
import { exportToExcel, exportToPDF } from '../../helpers/exportUtils';
import NoRecords from '../../components/Table/NoRecords';

const EmployeeBalanceReport = () => {
  const dispatch = useDispatch();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId = LocalstorageService.getItem(CompanyId);
  const today = new Date();
  const fmtDate = (d) => d.toISOString().slice(0, 10);

  // ── Results ──
  const [summaryData,   setSummaryData]   = useState([]);
  const [totalBalance,  setTotalBalance]  = useState(0);
  const [isLoading,     setIsLoading]     = useState(false);

  // ── Drill-down state ──
  const [expandedEmployee, setExpandedEmployee] = useState(null); // employeeId
  const [detailData,       setDetailData]       = useState([]);
  const [detailLoading,    setDetailLoading]     = useState(false);

  useEffect(() => {
    setPageTitle('יתרת עובדים');
    setCrumbItem([
      { title: 'ATM',    link: '#' },
      { title: 'דוחות', link: '#' },
      { title: 'יתרת עובדים', link: '#' },
    ]);
    loadReport();
  }, []);

  const loadReport = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const response = await reportService.getEmployeeBalanceReport({ companyId, employeeId: 0 });
      const { status, dataList, totalBalance: tb } = response.data;

      if (status === 200) {
        setSummaryData(dataList || []);
        setTotalBalance(tb || 0);
        if (!dataList?.length) toast.info('אין יתרות פתוחות לעובדים');
      } else {
        toast.error('שגיאה בטעינת הדוח');
      }
    } catch (e) {
      console.error('EmployeeBalanceReport error:', e);
      toast.error('שגיאה בטעינת הדוח');
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const handleToggleEmployee = async (employeeId) => {
    if (expandedEmployee === employeeId) {
      // collapse
      setExpandedEmployee(null);
      setDetailData([]);
      return;
    }

    setExpandedEmployee(employeeId);
    setDetailLoading(true);
    try {
      const response = await reportService.getEmployeeBalanceReport({ companyId, employeeId });
      const { status, detailList } = response.data;
      if (status === 200) {
        setDetailData(detailList || []);
      } else {
        toast.error('שגיאה בטעינת פירוט');
      }
    } catch (e) {
      toast.error('שגיאה בטעינת פירוט');
    } finally {
      setDetailLoading(false);
    }
  };

  const formatNum = (n) =>
    new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(n ?? 0);

  const summaryColumns = [
    { key: 'rowNum',          header: '#' },
    { key: 'employeeName',    header: 'עובד' },
    { key: 'phoneNumber',     header: 'טלפון' },
    { key: 'activeBatches',   header: 'אצוות פעילות' },
    { key: 'totalBalance',    header: 'יתרה לגבייה (₪)' },
    { key: 'oldestBatchDate', header: 'אצווה ותיקה מ' },
    { key: 'latestBatchDate', header: 'אצווה אחרונה' },
    { key: 'terminalNumbers', header: 'מסופים' },
  ];

  const handleExportExcel = () => {
    if (!summaryData.length) { toast.warning('אין נתונים לייצוא'); return; }
    const rows = summaryData.map((r, i) => ({ ...r, rowNum: i + 1 }));
    exportToExcel(rows, summaryColumns, `EmployeeBalance_${fmtDate(today)}`, 'יתרת עובדים');
    toast.success('הקובץ יורד בהצלחה');
  };

  const handleExportPDF = () => {
    if (!summaryData.length) { toast.warning('אין נתונים לייצוא'); return; }
    const rows = summaryData.map((r, i) => ({ ...r, rowNum: i + 1 }));
    exportToPDF(rows, summaryColumns, `EmployeeBalance_${fmtDate(today)}`, 'יתרת עובדים');
  };

  const balanceColor = (balance) => {
    if (balance >= 10000) return 'text-danger fw-bold';
    if (balance >= 3000)  return 'text-warning fw-bold';
    return 'text-success';
  };

  return (
    <div className="employee-balance-report" dir="rtl">
      {/* ── Header card ── */}
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h5 className="mb-1">💰 יתרת עובדים — כסף שטרם הוחזר לבנק</h5>
              <small className="text-muted">
                מציג עובדים עם TotalToReturn פעיל (טעינות שעדיין לא הופקדו לבנק)
              </small>
            </div>
            <Button color="primary" size="sm" onClick={loadReport} disabled={isLoading}>
              🔄 רענון
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ── Results ── */}
      {summaryData.length > 0 && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                <h5 className="mb-0">📋 {summaryData.length} עובדים עם יתרה פתוחה</h5>
                <span className="badge bg-danger fs-6">
                  סה"כ לגבייה: {formatNum(totalBalance)} ₪
                </span>
              </div>
              <div className="d-flex gap-2">
                <Button color="success" size="sm" onClick={handleExportExcel}>📥 Excel</Button>
                <Button color="danger"  size="sm" onClick={handleExportPDF}>📄 PDF</Button>
              </div>
            </div>

            <div className="table-responsive">
              <Table bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>#</th>
                    <th>עובד</th>
                    <th>טלפון</th>
                    <th className="text-center">אצוות פעילות</th>
                    <th className="text-end">יתרה לגבייה ₪</th>
                    <th>ותיק מ</th>
                    <th>אחרון</th>
                    <th>מסופים</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((row, idx) => (
                    <React.Fragment key={row.employeeId ?? idx}>
                      {/* Summary row */}
                      <tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleToggleEmployee(row.employeeId)}
                        className={expandedEmployee === row.employeeId ? 'table-active' : ''}
                      >
                        <td className="text-center">
                          {expandedEmployee === row.employeeId ? '▾' : '▸'}
                        </td>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{row.employeeName}</td>
                        <td>
                          {row.phoneNumber
                            ? <a href={`tel:${row.phoneNumber}`} onClick={(e) => e.stopPropagation()}>
                                {row.phoneNumber}
                              </a>
                            : '—'}
                        </td>
                        <td className="text-center">{row.activeBatches}</td>
                        <td className={`text-end ${balanceColor(row.totalBalance)}`}>
                          {formatNum(row.totalBalance)}
                        </td>
                        <td>{row.oldestBatchDate}</td>
                        <td>{row.latestBatchDate}</td>
                        <td style={{ maxWidth: 200, whiteSpace: 'normal', fontSize: '0.8em' }}>
                          {row.terminalNumbers || '—'}
                        </td>
                      </tr>

                      {/* Drill-down detail row */}
                      <tr style={{ padding: 0, border: 'none' }}>
                        <td colSpan={9} style={{ padding: 0, border: 'none' }}>
                          <Collapse isOpen={expandedEmployee === row.employeeId}>
                            <div className="p-3 bg-light border-start border-4 border-primary">
                              {detailLoading && expandedEmployee === row.employeeId
                                ? <div className="text-center py-2">טוען פירוט...</div>
                                : detailData.length > 0
                                  ? (
                                    <Table bordered size="sm" className="mb-0 bg-white">
                                      <thead className="table-secondary">
                                        <tr>
                                          <th>מסוף</th>
                                          <th>מקום</th>
                                          <th>אצווה</th>
                                          <th>תאריך טעינה</th>
                                          <th className="text-end">יתרה ₪</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detailData.map((d, di) => (
                                          <tr key={d.id ?? di}>
                                            <td>{d.terminalNumber}</td>
                                            <td>{d.businessName}</td>
                                            <td>{d.batch}</td>
                                            <td>{d.loadDate}</td>
                                            <td className="text-end fw-bold text-danger">
                                              {formatNum(d.totalToReturn)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="table-secondary fw-bold">
                                        <tr>
                                          <td colSpan={4} className="text-end">סה"כ:</td>
                                          <td className="text-end text-danger">
                                            {formatNum(detailData.reduce((s, d) => s + (d.totalToReturn || 0), 0))}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </Table>
                                  )
                                  : <div className="text-muted small">אין פירוט זמין</div>
                              }
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="table-secondary fw-bold">
                  <tr>
                    <td colSpan={5} className="text-end">סה"כ לגבייה:</td>
                    <td className="text-end text-danger">{formatNum(totalBalance)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </Table>
            </div>

            {/* Legend */}
            <div className="mt-2 d-flex flex-wrap gap-3 small text-muted">
              <span><span className="text-danger fw-bold">■</span> מעל 10,000 ₪ — גבוה</span>
              <span><span className="text-warning fw-bold">■</span> מעל 3,000 ₪ — בינוני</span>
              <span><span className="text-success">■</span> פחות מ-3,000 ₪ — תקין</span>
              <span className="me-3 text-info">💡 לחץ על שורה לפירוט לפי מסוף/אצווה</span>
            </div>
          </CardBody>
        </Card>
      )}

      {!isLoading && summaryData.length === 0 && (
        <Card>
          <CardBody>
            <div className="text-center py-4 text-success">
              <div style={{ fontSize: '3rem' }}>✅</div>
              <h5 className="mt-2">כל הכסף הוחזר לבנק!</h5>
              <small className="text-muted">אין עובדים עם יתרה פעילה</small>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default EmployeeBalanceReport;
